import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { Employee, Vehicle } from '@fc-core/models';
import { Company } from '@fc-crm/models/company.model';
import { Contact } from '@fc-crm/models/contact.model';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import { ImageViewComponent } from '@fc-shared/ui/image-loader/image-view.component';
import { AvatarPlaceholderComponent } from './avatar-placeholder.component';
import { IconComponent } from '../icon/icon.component';

export type EntityAvatarType =
  | 'asset'
  | 'company'
  | 'contact'
  | 'employee'
  | 'vehicle';

export type CompanyAvatarData = Omit<Company, 'photo'> & {
  photo?: string | AttachmentModel | null;
};
export type ContactAvatarData = Omit<Contact, 'photo'> & {
  photo?: string | AttachmentModel | null;
};
export type EmployeeAvatarData = Omit<Employee, 'avatar'> & {
  avatar?: string | AttachmentModel | null;
};
export type VehicleAvatarData = Partial<Vehicle>;
export type AssetAvatarData = {
  name?: string | null;
  photo?: string | AttachmentModel | null;
  primaryPhoto?: string | AttachmentModel | null;
};

export type EntityAvatarData =
  | AssetAvatarData
  | CompanyAvatarData
  | ContactAvatarData
  | EmployeeAvatarData
  | VehicleAvatarData;

/** The subset of fields the avatar reads, across all entity types. */
interface AvatarEntityFields {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  identifier?: string | null;
  color?: string | null;
  photo?: string | AttachmentModel | null;
  primaryPhoto?: string | AttachmentModel | null;
  avatar?: string | AttachmentModel | null;
}

@Component({
  selector: 'fc-entity-avatar',
  template: `
    @if (photoAttachment()) {
      <fc-image-view
        [photo]="photoAttachment()"
        [shape]="imageShape()"
        [size]="size()"
        [cornerRadius]="cornerRadius()"
        [alt]="altText()"
      />
    } @else if (imageUrl()) {
      <fc-image-view
        [image]="imageUrl()"
        [shape]="imageShape()"
        [size]="size()"
        [cornerRadius]="cornerRadius()"
        [alt]="altText()"
      />
    } @else if (type() === 'asset') {
      <div
        class="flex items-center justify-center bg-secondary-100"
        [style.width.px]="size()"
        [style.height.px]="size()"
        [style.borderRadius.px]="cornerRadius() ?? 4"
      >
        <fc-icon
          icon="icon-tool"
          color="secondary400"
          [size]="assetIconSize()"
        />
      </div>
    } @else if (type() === 'vehicle' && !entity()) {
      <div
        class="flex items-center justify-center"
        [style.width.px]="size()"
        [style.height.px]="size()"
      >
        <fc-icon icon="icon-vehicles-bold" color="secondary400" [size]="20" />
      </div>
    } @else {
      <fc-avatar-placeholder
        [color]="color()"
        [fontSize]="fontSize()"
        [size]="size()"
        [text]="initials()"
        [shape]="resolvedShape()"
      />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ImageViewComponent, AvatarPlaceholderComponent, IconComponent],
})
export class EntityAvatarComponent {
  readonly type = input.required<EntityAvatarType>();
  readonly entity = input<EntityAvatarData | null>();
  readonly size = input.required<number>();
  readonly fontSize = input<number>();
  /** Overrides the per-type default: circle for contact/employee, square for company/vehicle. */
  readonly shape = input<'circle' | 'square'>();
  /** Overrides the square shape's default 4px corner radius. */
  readonly cornerRadius = input<number | null>(null);

  private readonly fields = computed<AvatarEntityFields>(
    () => (this.entity() ?? {}) as AvatarEntityFields,
  );
  private readonly rawPhoto = computed(() => {
    const fields = this.fields();
    if (this.type() === 'employee') return fields.avatar;
    return fields.photo ?? fields.primaryPhoto;
  });

  protected readonly photoAttachment = computed<AttachmentModel | null>(() => {
    const photo = this.rawPhoto();
    return photo && typeof photo === 'object' ? photo : null;
  });
  protected readonly imageUrl = computed<string | null>(() => {
    const photo = this.rawPhoto();
    return typeof photo === 'string' ? photo : null;
  });
  protected readonly initials = computed<string>(() => {
    const fields = this.fields();
    switch (this.type()) {
      case 'company':
        return fields.name?.charAt(0) || '';
      case 'vehicle':
        return (fields.identifier || '').trim().slice(0, 2).toUpperCase();
      default:
        return (
          (fields.firstName?.charAt(0) || '') +
          (fields.lastName?.charAt(0) || '')
        );
    }
  });
  protected readonly color = computed(() =>
    this.type() === 'vehicle' ? undefined : (this.fields().color ?? undefined),
  );
  protected readonly assetIconSize = computed(() =>
    Math.round(this.size() * 0.55),
  );
  protected readonly resolvedShape = computed<'circle' | 'square'>(
    () =>
      this.shape() ??
      (this.type() === 'contact' || this.type() === 'employee'
        ? 'circle'
        : 'square'),
  );
  protected readonly imageShape = computed<'circle' | 'rectangle'>(() =>
    this.resolvedShape() === 'circle' ? 'circle' : 'rectangle',
  );
  protected readonly altText = computed(() => {
    switch (this.type()) {
      case 'vehicle':
        return this.fields().identifier || 'photo';
      case 'asset':
        return this.fields().name || 'photo';
      default:
        return 'photo';
    }
  });
}
